/// <reference types="vite/client" />
import {createRouter, createWebHistory, type RouteRecordRaw} from "vue-router"
import auth from "./auth/keycloak";
import {Role} from "./graphql/types";
import {isChunkLoadError} from "./chunk-error";
import {captureException} from "@sentry/vue";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: () => import("./dashboard/Dashboard.vue"),
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('./search/SearchView.vue'),
  },
  {
    path: '/aufbereitung',
    name: 'aufbereitung-list',
    component: () =>
      [Role.MANAGE_ARTICLES, Role.MANAGE_OWN_ARTICLES, Role.MANAGE_GAMES, Role.MANAGE_COMPANIES,
       Role.MANAGE_AUTHORS, Role.MANAGE_GAME_CONSOLES, Role.MANAGE_GENRES, Role.MANAGE_GAME_SERIES,
       Role.MANAGE_MEDIA].some((r) => auth.hasRole(r))
        ? import('./aufbereitung/AufbereitungListView.vue')
        : import('./NoAccess.vue'),
  },
  {
    path: '/articles',
    name: 'article-list',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES))
      ? import('./articles/ArticleListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/statistik',
    name: 'statistik',
    component: () => auth.hasRole(Role.MANAGE_ARTICLES)
      ? import('./statistik/StatistikView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/import',
    name: 'article-import',
    component: () => auth.hasRole(Role.MANAGE_ARTICLES)
      ? import('./articles/ArticleImportView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/:id',
    name: 'article',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES))
      ? import('./articles/ArticleEditView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/:id/comments',
    name: 'article-comments',
    component: () => (auth.hasRole('MANAGE_COMMENTS') || auth.hasRole(Role.MANAGE_ARTICLES))
      ? import('./comments/ArticleCommentsView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/:id/analyses',
    name: 'article-analyses',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES))
      ? import('./articles/analysis/ArticleAnalysisListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/:id/analysis/:analysisId',
    name: 'article-analysis',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES))
      ? import('./articles/analysis/ArticleAnalysisDetailView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/articles/:id/history',
    name: 'article-history',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES))
      ? import('./articles/ArticleHistoryView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: "/community",
    name: "community",
    component: () => import("./community/CommunityView.vue"),
  },
  {
    path: '/quick-filters',
    name: 'quick-filter-list',
    component: () => (auth.hasRole(Role.MANAGE_QUICK_FILTER) || auth.hasRole(Role.MANAGE_OWN_QUICK_FILTER))
      ? import('./quick-filters/QuickFilterList.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/quick-filters/new',
    name: 'quick-filter-create',
    component: () => (auth.hasRole(Role.MANAGE_QUICK_FILTER) || auth.hasRole(Role.MANAGE_OWN_QUICK_FILTER))
      ? import('./quick-filters/QuickFilter.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/quick-filters/:id',
    name: 'quick-filter-edit',
    component: () => (auth.hasRole(Role.MANAGE_QUICK_FILTER) || auth.hasRole(Role.MANAGE_OWN_QUICK_FILTER))
      ? import('./quick-filters/QuickFilter.vue')
      : import('./NoAccess.vue'),
    props: true,
  },
  {
    path: '/rating-categories',
    name: 'rating-category-list',
    component: () => (auth.hasRole(Role.MANAGE_RATING_CATEGORIES) || auth.hasRole(Role.MANAGE_OWN_RATING_CATEGORIES))
      ? import('./rating-categories/RatingCategoryListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/rating-categories/sort',
    name: 'rating-category-sort',
    component: () => auth.hasRole(Role.MANAGE_RATING_CATEGORIES)
      ? import('./rating-categories/RatingCategorySortView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/rating-categories/:id',
    name: 'rating-category',
    component: () => (auth.hasRole(Role.MANAGE_RATING_CATEGORIES) || auth.hasRole(Role.MANAGE_OWN_RATING_CATEGORIES))
      ? import('./rating-categories/RatingCategory.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/games',
    name: 'game-list',
    component: () => (auth.hasRole(Role.MANAGE_GAMES) || auth.hasRole(Role.MANAGE_OWN_GAMES))
      ? import('./games/GameList.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/games/:id',
    name: 'game',
    component: () => (auth.hasRole(Role.MANAGE_GAMES) || auth.hasRole(Role.MANAGE_OWN_GAMES))
      ? import('./games/Game.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/game-series',
    name: 'game-series-list',
    component: () => (auth.hasRole(Role.MANAGE_GAME_SERIES) || auth.hasRole(Role.MANAGE_OWN_GAME_SERIES))
      ? import('./gameseries/GameSeriesListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/game-series/:id',
    name: 'game-series',
    component: () => (auth.hasRole(Role.MANAGE_GAME_SERIES) || auth.hasRole(Role.MANAGE_OWN_GAME_SERIES))
      ? import('./gameseries/GameSeries.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/article-series',
    name: 'article-series-list',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLE_SERIES) || auth.hasRole(Role.MANAGE_OWN_ARTICLE_SERIES))
      ? import('./articleseries/ArticleSeriesList.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/article-series/:id',
    name: 'article-series',
    component: () => (auth.hasRole(Role.MANAGE_ARTICLE_SERIES) || auth.hasRole(Role.MANAGE_OWN_ARTICLE_SERIES))
      ? import('./articleseries/ArticleSeries.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/events',
    name: 'event-list',
    component: () => (auth.hasRole(Role.MANAGE_EVENTS) || auth.hasRole(Role.MANAGE_OWN_EVENTS))
      ? import('./events/EventListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/events/:id',
    name: 'event',
    component: () => (auth.hasRole(Role.MANAGE_EVENTS) || auth.hasRole(Role.MANAGE_OWN_EVENTS))
      ? import('./events/Event.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/genres',
    name: 'genre-list',
    component: () => (auth.hasRole(Role.MANAGE_GENRES) || auth.hasRole(Role.MANAGE_OWN_GENRES))
      ? import('./genre/GenreListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/genres/:id',
    name: 'genre',
    component: () => (auth.hasRole(Role.MANAGE_GENRES) || auth.hasRole(Role.MANAGE_OWN_GENRES))
      ? import('./genre/Genre.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/game-consoles',
    name: 'game-console-list',
    component: () => (auth.hasRole(Role.MANAGE_GAME_CONSOLES) || auth.hasRole(Role.MANAGE_OWN_GAME_CONSOLES))
      ? import('./gameconsole/GameConsoleListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/game-consoles/:id',
    name: 'game-console',
    component: () => (auth.hasRole(Role.MANAGE_GAME_CONSOLES) || auth.hasRole(Role.MANAGE_OWN_GAME_CONSOLES))
      ? import('./gameconsole/GameConsole.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/companies',
    name: 'company-list',
    component: () => (auth.hasRole(Role.MANAGE_COMPANIES) || auth.hasRole(Role.MANAGE_OWN_COMPANIES))
      ? import('./companies/CompanyListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/companies/:id',
    name: 'company',
    component: () => (auth.hasRole(Role.MANAGE_COMPANIES) || auth.hasRole(Role.MANAGE_OWN_COMPANIES))
      ? import('./companies/Company.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/media',
    name: 'media-list',
    component: () => (auth.hasRole(Role.MANAGE_MEDIA) || auth.hasRole(Role.MANAGE_OWN_MEDIA))
      ? import('./media/MediaListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/media/:id',
    name: 'media',
    component: () => (auth.hasRole(Role.MANAGE_MEDIA) || auth.hasRole(Role.MANAGE_OWN_MEDIA))
      ? import('./media/Media.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/tags',
    name: 'tag-list',
    component: () => (auth.hasRole(Role.MANAGE_TAG) || auth.hasRole(Role.MANAGE_OWN_TAG))
      ? import('./tag/TagListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/tags/:id',
    name: 'tag',
    component: () => (auth.hasRole(Role.MANAGE_TAG) || auth.hasRole(Role.MANAGE_OWN_TAG))
      ? import('./tag/Tag.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: '/videos',
    name: 'youtube-video-list',
    component: () => auth.hasRole(Role.MANAGE_MEDIA)
      ? import('./youtube/YouTubeVideoListView.vue')
      : import('./NoAccess.vue'),
  },
  {
    path: "/authors",
    name: "author-list",
    component: () => (auth.hasRole(Role.MANAGE_AUTHORS) || auth.hasRole(Role.MANAGE_OWN_AUTHORS))
      ? import("./author/AuthorListView.vue")
      : import('./NoAccess.vue'),
  },
  {
    path: "/authors/:id",
    name: "author",
    component: () => (auth.hasRole(Role.MANAGE_AUTHORS) || auth.hasRole(Role.MANAGE_OWN_AUTHORS))
      ? import("./author/Author.vue")
      : import('./NoAccess.vue'),
  },
  {
    path: "/users",
    name: "user-list",
    component: () => auth.hasRole(Role.MANAGE_USERS)
      ? import("./users/UserListView.vue")
      : import('./NoAccess.vue'),
  },
  {
    path: "/users/:sub",
    name: "user",
    component: () => auth.hasRole(Role.MANAGE_USERS)
      ? import("./users/UserDetailView.vue")
      : import('./NoAccess.vue'),
  },
  {
    path: "/system",
    name: "system",
    component: () => auth.hasRole(Role.SYSTEM_ADMINISTRATION)
      ? import("./system/System.vue")
      : import('./NoAccess.vue'),
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import('./NoAccess.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    // always scroll to top
    return {top: 0}
  },
})

// Recover from stale lazy-loaded chunks after a deploy: the old index.html
// references chunk hashes that no longer exist, so the dynamic import 404s and
// the navigation silently dies. Hard-navigate to the target so the browser
// fetches a fresh document with current chunk hashes (GlitchTip #31/#390/#383).
router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    window.location.href = to.fullPath
  } else {
    // Registering onError marks the navigation error as handled, so any non-chunk
    // failure (auth guard, lazy component) would otherwise vanish silently. Report it.
    captureException(error, { extra: { to: to.fullPath } })
  }
})

export default router
